import type { DefaultError, UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { ToastMessageWithTitle } from '../components/toast'
import { useToastService } from '../services/toast-service'
import { ApiError } from '../utils/api-client/api-error'
import { ErrorDetail } from '../utils/error-handling/error-detail'
import { getErrorMessage, getErrorTitle } from '../utils/error-handling/utils'

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- only used in generic constraint clauses; `unknown` causes assignability failures due to contravariance in UseMutationResult's `mutate` parameter
export type MutationHookConstraint = (...args: any[]) => UseMutationResult<any, DefaultError, any, any>

export type DefaultMutationHook = (...args: unknown[]) => UseMutationResult

type InferOnErrorParams<TMutation extends MutationHookConstraint = DefaultMutationHook> = TMutation extends (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- `any[]` is required; `unknown[]` breaks conditional type matching due to function parameter contravariance
  ...args: any[]
) => UseMutationResult<infer TData, infer TError, infer TVariables, infer TContext>
  ? Parameters<NonNullable<UseMutationOptions<TData, TError, TVariables, TContext>['onError']>>
  : never

export type PrefixFn<TMutation extends MutationHookConstraint = DefaultMutationHook> = (
  ...args: InferOnErrorParams<TMutation>
) => string

export type ToastOverride = {
  message: React.ReactNode
  transience?: 'sticky'
}

/**
 * The 403 toast for an admin-gated write. Reachable with the control hidden: the role can be revoked,
 * or enforcement turned on, while the form is open. Sticky, because waiting won't fix a missing
 * permission.
 */
export const forbiddenOverride = (message: React.ReactNode): ToastOverride => ({ message, transience: 'sticky' })

type UseShowMutationErrorOptions<TMutation extends MutationHookConstraint = DefaultMutationHook> = {
  prefix?: string | PrefixFn<TMutation>
  /** Keep this referentially stable (a module-level constant): `showMutationError` is memoized on it. */
  overrides?: Record<number, ToastOverride>
  /**
   * A message for errors the shared one describes poorly; return undefined to keep the shared one.
   * Keep this referentially stable, like `overrides`.
   */
  getMessage?: (error: DefaultError) => string | undefined
}

export function useShowMutationError<TMutation extends MutationHookConstraint = DefaultMutationHook>({
  prefix,
  overrides,
  getMessage,
}: UseShowMutationErrorOptions<TMutation> = {}) {
  const { addToast } = useToastService()

  const showMutationError = useCallback(
    (...args: InferOnErrorParams<TMutation>) => {
      const [error] = args
      const override = error instanceof ApiError ? overrides?.[error.status] : undefined
      if (override) {
        addToast({
          message: <>{override.message}</>,
          variant: 'error',
          transience: override.transience,
        })
        return
      }

      const resolvedPrefix =
        typeof prefix === 'function' ? prefix(...args) : typeof prefix === 'string' ? prefix : undefined
      const rawTitle = getErrorTitle(error, resolvedPrefix)
      const rawMessage = getMessage?.(error) ?? getErrorMessage(error)
      const title = rawTitle === rawMessage ? undefined : rawTitle
      const detail = <ErrorDetail error={error} message={rawMessage} />

      addToast({
        message: <ToastMessageWithTitle title={title} detail={detail} />,
        variant: 'error',
      })
    },
    [addToast, getMessage, overrides, prefix]
  )

  return showMutationError
}
